// Enhanced CODTournamentSystem.js - Call of Duty Mobile Style with Compact Header
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import MatchHistory from './profile/MatchHistory';
import { useNavigate } from 'react-router-dom';

import { 
  Users, Trophy, DollarSign, Plus, Shield, CreditCard, Settings, 
  Globe, Clock, MessageCircle, Eye, EyeOff, UserPlus, X, Minimize2, 
  Send, AlertCircle, Play, Trash2, UserMinus, Crown, Award, Star,
  CheckCircle, AlertTriangle, Activity, Calendar, Target, Inbox, Menu,
  Bot, Zap, LogOut, User, Package
} from 'lucide-react';

// Import enhanced translation system
import { translations, languageConfig, getTranslation, isRTL } from '../translations/translations';

// Import sub-components
import LoginForm from './auth/LoginForm';
import RegisterForm from './auth/RegisterForm';
import BanModal from './modals/BanModal';
import TournamentsList from './tournaments/TournamentsList';
import CreateTournament from './tournaments/CreateTournament';
import ProfileManagement from './profile/ProfileManagement';
import PointsManagement from './profile/PointsManagement';
import AdminPanel from './admin/AdminPanel';
import LiveChat from './chat/LiveChat';
import PayPalModal from './payments/PayPalModal';
import InboxSystem from './inbox/InboxSystem';
import VendorDashboard from './vendor/VendorDashboard';
import KickModal from './modals/KickModal';

// Import Bracket Tournament components
import { BracketTournamentCreate, BracketTournamentCard } from './tournaments/BracketTournament';
// Import BR Multistep Tournament components
import { MultistepTypeSelector, BRMultistepCreate, BRMultistepCard } from './tournaments/BRMultistep';
// Import FFA Multistep Tournament components
import { FFAMultistepCard, FFAMultistepCreate } from './tournaments/FFAMultistep';

// Import Level System components
import LevelDisplay from './LevelSystem/LevelDisplay';
import LevelProgress from './LevelSystem/LevelProgress';
import BadgeDisplay from './LevelSystem/BadgeDisplay';

// Enhanced Filler Modal Component with BR Team Support
const AddFillerModal = ({ tournament, onClose, onFillerAdded, user, t }) => {
  const [fillerName, setFillerName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(false);

  const fillerCost = tournament?.tournamentType === 'giveaway' ? 0 : tournament?.entryFee || 0;

  // Check if this is a BR Duo/Squad tournament
  const isBRTeamTournament = tournament?.gameMode === 'battleRoyale' &&
    ['duo', 'squad'].includes(tournament?.multiplayerType);

  const maxTeamSize = tournament?.multiplayerType === 'duo' ? 2 : 4;

  // Get user's current team in BR tournament
  const userTeamNumber = React.useMemo(() => {
    if (!isBRTeamTournament || !tournament?.brTeamAssignments) return null;
    const assignment = tournament.brTeamAssignments.find(
      a => (a.userId?._id || a.userId)?.toString() === user._id?.toString()
    );
    return assignment?.teamNumber || null;
  }, [isBRTeamTournament, tournament?.brTeamAssignments, user._id]);

  // Get available teams for filler (only user's team in BR)
  const availableTeams = React.useMemo(() => {
    if (!isBRTeamTournament || !tournament) return [];

    const teamCount = Math.ceil(tournament.maxPlayers / maxTeamSize);
    const teams = [];

    for (let i = 1; i <= teamCount; i++) {
      const teamMembers = (tournament.brTeamAssignments || [])
        .filter(a => a.teamNumber === i);
      const spotsRemaining = maxTeamSize - teamMembers.length;

      // Only show user's team with available spots
      if (i === userTeamNumber && spotsRemaining > 0) {
        teams.push({
          teamNumber: i,
          members: teamMembers.map(a => ({
            name: a.inGameName || a.username || 'Unknown',
            isFiller: a.isFiller
          })),
          spotsRemaining
        });
      }
    }

    return teams;
  }, [isBRTeamTournament, tournament, maxTeamSize, userTeamNumber]);

  // Auto-select user's team for BR
  React.useEffect(() => {
    if (isBRTeamTournament && userTeamNumber && availableTeams.length > 0) {
      setSelectedTeam(userTeamNumber);
    }
  }, [isBRTeamTournament, userTeamNumber, availableTeams]);

  const canAddFiller = isBRTeamTournament
    ? availableTeams.length > 0 && userTeamNumber
    : true;

  const handleAddFiller = async (e) => {
    e.preventDefault();

    if (!fillerName.trim()) {
      toast.error(t('fillerNameRequired', 'Filler name is required'));
      return;
    }

    if (fillerName.trim().length > 20) {
      toast.error(t('fillerNameTooLong', 'Filler name too long (max 20 characters)'));
      return;
    }

    if (fillerCost > 0 && user.points < fillerCost) {
      toast.error(t('insufficientPointsForFiller', 'Insufficient points to add filler'));
      return;
    }

    if (isBRTeamTournament && !selectedTeam) {
      toast.error(t('selectTeamRequired', 'Please select a team for the filler'));
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        fillerName: fillerName.trim()
      };

      // Add team number for BR tournaments
      if (isBRTeamTournament && selectedTeam) {
        requestData.teamNumber = selectedTeam;
      }

      const response = await axios.post(`/api/tournaments/${tournament._id}/add-filler`, requestData);

      toast.success(t('fillerAddedSuccessfully', 'Filler "{name}" added successfully!')
        .replace('{name}', fillerName.trim()));

      onFillerAdded(response.data);
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToAddFiller', 'Failed to add filler');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!tournament) return null;

  return (
    <div className="filler-modal-overlay" style={{ zIndex: 9999 }}>
      <div className="filler-modal card">
        <div className="filler-modal-header">
          <div className="filler-modal-icon">
            <Bot className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="filler-modal-title military-font text-glow">{t('addFiller', 'Add Filler Player')}</h3>
          <button onClick={onClose} className="filler-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="filler-modal-content">
          <div className="filler-info-section card" style={{ marginBottom: '1rem' }}>
            <div className="filler-info-item">
              <span className="filler-info-label">{t('tournament', 'Tournament')}:</span>
              <span className="filler-info-value text-glow">{tournament.gameMode} - {tournament.map}</span>
            </div>
            <div className="filler-info-item">
              <span className="filler-info-label">{t('slots', 'Slots')}:</span>
              <span className="filler-info-value text-glow">
                {tournament.actualParticipantCount || tournament.participants?.length || 0}/{tournament.maxPlayers}
              </span>
            </div>
            <div className="filler-info-item">
              <span className="filler-info-label">{t('cost', 'Cost')}:</span>
              <span className={`filler-info-value text-glow ${fillerCost === 0 ? 'filler-cost-free' : 'filler-cost-paid'}`}>
                {fillerCost === 0 ? t('free', 'FREE') : `${fillerCost} ${t('points', 'points')}`}
              </span>
            </div>
          </div>

          {/* BR Team Section */}
          {isBRTeamTournament && (
            <div className="filler-team-section card" style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(79, 172, 254, 0.1)', border: '1px solid rgba(79, 172, 254, 0.3)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#4facfe', fontWeight: '600' }}>
                <Users className="w-4 h-4" />
                <span>{t('yourTeam', 'Your Team')}</span>
              </div>

              {!userTeamNumber ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', color: '#fca5a5' }}>
                  <AlertCircle className="w-4 h-4" />
                  <span>{t('notInTeam', 'You must join a team before adding fillers')}</span>
                </div>
              ) : availableTeams.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', color: '#fca5a5' }}>
                  <AlertCircle className="w-4 h-4" />
                  <span>{t('teamFull', 'Your team is full')}</span>
                </div>
              ) : (
                <div>
                  {availableTeams.map(team => (
                    <div
                      key={team.teamNumber}
                      onClick={() => setSelectedTeam(team.teamNumber)}
                      style={{
                        padding: '12px',
                        background: selectedTeam === team.teamNumber ? 'rgba(79, 172, 254, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                        border: selectedTeam === team.teamNumber ? '1px solid #4facfe' : '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '700', color: '#4facfe' }}>T{String(team.teamNumber).padStart(2, '0')}</span>
                        <span style={{ fontSize: '0.8rem', color: '#10b981' }}>{team.spotsRemaining} {t('spotsAvailable', 'spots available')}</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {team.members.map((member, idx) => (
                          <span key={idx} style={{ fontSize: '0.8rem', padding: '4px 8px', background: member.isFiller ? 'rgba(147, 51, 234, 0.2)' : 'rgba(255, 255, 255, 0.1)', borderRadius: '6px', color: member.isFiller ? '#c4b5fd' : '#e5e7eb' }}>
                            {member.isFiller ? '🤖' : '👤'} {member.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleAddFiller} className="filler-form">
            <div className="filler-form-field">
              <label className="filler-form-label military-font">
                {t('fillerName', 'Filler Player Name')}
              </label>
              <input
                type="text"
                value={fillerName}
                onChange={(e) => setFillerName(e.target.value)}
                placeholder={t('enterFillerName', 'Enter filler name...')}
                className="filler-form-input tactical-border"
                maxLength={20}
                disabled={loading || !canAddFiller}
                autoFocus
              />
              <div className="filler-form-help">
                {t('fillerNameHelp', 'Choose a unique name for this AI player (max 20 characters)')}
              </div>
            </div>

            {fillerCost > 0 && (
              <div className="filler-cost-warning card">
                <AlertCircle className="w-4 h-4" />
                <span>
                  {t('fillerCostWarning', 'You will pay {cost} points for this filler. If the filler wins, you get the prize!')
                    .replace('{cost}', fillerCost)}
                </span>
              </div>
            )}

            {tournament.tournamentType === 'giveaway' && (
              <div className="filler-giveaway-info card">
                <CheckCircle className="w-4 h-4" />
                <span>
                  {t('giveawayFillerFree', 'This is a giveaway tournament - fillers are FREE!')}
                </span>
              </div>
            )}

            <div className="filler-form-actions">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="filler-btn filler-btn-cancel tactical-border"
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !fillerName.trim() || (fillerCost > 0 && user.points < fillerCost) || !canAddFiller || (isBRTeamTournament && !selectedTeam)}
                className="filler-btn filler-btn-add tactical-border border-glow"
              >
                {loading ? (
                  <>
                    <Zap className="w-4 h-4 animate-spin" />
                    {t('adding', 'Adding...')}
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4" />
                    {t('addFiller', 'Add Filler')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const CODTournamentSystem = ({ 
  user, 
  socket, 
  onlineUsers = [], 
  onLogin, 
  onRegister, 
  onLogout, 
  onUserUpdate,
  connectionStatus = 'disconnected',
  socketReady = false
}) => {
  // Enhanced language state management
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('preferred-language');
    return savedLanguage || 'en';
  });
  
  // Add React Router navigation
  const navigate = useNavigate();
  
  const [currentView, setCurrentView] = useState('matches');
  const [showBanModal, setShowBanModal] = useState(false);
  const [banModalData, setBanModalData] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [showRoleChangeNotification, setShowRoleChangeNotification] = useState(false);
  const [roleChangeData, setRoleChangeData] = useState(null);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Mobile chat state management
  const [isMobileChatActive, setIsMobileChatActive] = useState(false);
  const [mobileChatUnreadCount, setMobileChatUnreadCount] = useState(0);
  
  // Filler modal state
  const [showFillerModal, setShowFillerModal] = useState(false);
  const [fillerModalTournament, setFillerModalTournament] = useState(null);
  
  // Kick modal state
  const [kickModal, setKickModal] = useState({
    show: false,
    participant: null,
    tournament: null,
    loading: false
  });
  
  // Level system state
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [levelUpNotification, setLevelUpNotification] = useState(null);
  const [badgeNotification, setBadgeNotification] = useState(null);
  const [expNotification, setExpNotification] = useState(null);
  
  // Data states
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [adminStats, setAdminStats] = useState({});
  const [commissionStats, setCommissionStats] = useState({});

  // Bracket tournament states
  const [bracketTournaments, setBracketTournaments] = useState([]);
  const [showBracketCreate, setShowBracketCreate] = useState(false);
  const [showBracketSection, setShowBracketSection] = useState(false);
  const [showMultistepTypeSelector, setShowMultistepTypeSelector] = useState(false);
  const [showBRMultistepCreate, setShowBRMultistepCreate] = useState(false);
  const [brMultistepTournaments, setBRMultistepTournaments] = useState([]);
  const [ffaMultistepTournaments, setFFAMultistepTournaments] = useState([]);
  const [showFFAMultistepCreate, setShowFFAMultistepCreate] = useState(false);

  // Enhanced translation function
  const t = useCallback((key, fallback = null) => {
    return getTranslation(key, currentLanguage, fallback);
  }, [currentLanguage]);

  // Enhanced level title function
  const getLevelTitle = useCallback((level) => {
    if (level >= 100) return t('legend', 'Legend');
    if (level >= 90) return t('sage', 'Sage');
    if (level >= 80) return t('mythic', 'Mythic');
    if (level >= 70) return t('grandmaster', 'Grandmaster');
    if (level >= 60) return t('master', 'Master');
    if (level >= 50) return t('expert', 'Expert');
    if (level >= 40) return t('professional', 'Professional');
    if (level >= 30) return t('advanced', 'Advanced');
    if (level >= 20) return t('intermediate', 'Intermediate');
    if (level >= 10) return t('novice', 'Novice');
    return t('beginner', 'Beginner');
  }, [t]);

  // Calculate user level from available data
  const getUserLevel = useCallback(() => {
    // Debug: Log user object to see available fields
    if (process.env.NODE_ENV === 'development') {
      console.log('User object fields:', {
        level: user.level,
        currentLevel: user.currentLevel,
        totalExp: user.totalExp,
        exp: user.exp,
        allFields: Object.keys(user)
      });
    }
    
    // Try different possible field names and fallbacks
    if (user.currentLevel) return user.currentLevel;
    if (user.level) return user.level;
    
    // Calculate from total EXP if available
    if (user.totalExp !== undefined) {
      return calculateLevelFromExp(user.totalExp);
    }
    
    // Calculate from exp field
    if (user.exp !== undefined) {
      return calculateLevelFromExp(user.exp);
    }
    
    return 1; // Default fallback
  }, [user]);

  const getUserLevelTitle = useCallback(() => {
    if (user.levelTitle) return user.levelTitle;
    if (user.title) return user.title;
    return getLevelTitle(getUserLevel());
  }, [user, getLevelTitle, getUserLevel]);

  // Simple level calculation based on EXP (matching server logic)
  const calculateLevelFromExp = useCallback((totalExp) => {
    if (!totalExp || totalExp < 0) return 1;
    
    // Level structure from server: levels 1-5: 50 EXP each, etc.
    const levelStructure = [
      ...Array(5).fill(50),      // Levels 1-5: 50 EXP each
      ...Array(5).fill(75),      // Levels 6-10: 75 EXP each  
      ...Array(10).fill(100),    // Levels 11-20: 100 EXP each
      ...Array(10).fill(200),    // Levels 21-30: 200 EXP each
      ...Array(20).fill(300),    // Levels 31-50: 300 EXP each
      ...Array(30).fill(500),    // Levels 51-80: 500 EXP each
      ...Array(10).fill(1000),   // Levels 81-90: 1000 EXP each
      ...Array(10).fill(2000)    // Levels 91-100: 2000 EXP each
    ];
    
    let currentLevel = 1;
    let expUsed = 0;
    
    for (let level = 1; level <= 100; level++) {
      const expNeededForThisLevel = levelStructure[level - 1] || 0;
      
      if (expUsed + expNeededForThisLevel <= totalExp) {
        expUsed += expNeededForThisLevel;
        currentLevel = level + 1;
      } else {
        break;
      }
    }
    
    return Math.min(currentLevel, 100);
  }, []);

  // Get level progress percentage
  const getLevelProgress = useCallback(() => {
    const totalExp = user.totalExp || user.exp || 0;
    const currentLevel = getUserLevel();
    
    if (currentLevel >= 100) return 100;
    
    // Calculate progress to next level
    const levelStructure = [
      ...Array(5).fill(50), ...Array(5).fill(75), ...Array(10).fill(100),
      ...Array(10).fill(200), ...Array(20).fill(300), ...Array(30).fill(500),
      ...Array(10).fill(1000), ...Array(10).fill(2000)
    ];
    
    let expForCurrentLevel = 0;
    for (let i = 0; i < currentLevel - 1; i++) {
      expForCurrentLevel += levelStructure[i] || 0;
    }
    
    const expNeededForNext = levelStructure[currentLevel - 1] || 0;
    const currentLevelProgress = totalExp - expForCurrentLevel;
    
    return expNeededForNext > 0 ? (currentLevelProgress / expNeededForNext) * 100 : 0;
  }, [user, getUserLevel]);

  // Helper function to clean admin/approver suffix from names
  const cleanAdminName = useCallback((name) => {
    if (!name) return name;
    
    const suffixes = [
      'ADMIN', 'APPROVER', 'SUPERVISOR',
      'admin', 'approver', 'supervisor',
      '_admin', '_approver', '_supervisor',
      '[admin]', '[approver]', '[supervisor]',
      '(admin)', '(approver)', '(supervisor)'
    ];
    
    let cleaned = name;
    
    for (const suffix of suffixes) {
      if (cleaned.toLowerCase().endsWith(suffix.toLowerCase())) {
        cleaned = cleaned.substring(0, cleaned.length - suffix.length);
        break;
      }
    }
    
    return cleaned.trim();
  }, []);

  // Available languages with enhanced styling
  const languages = [
    {
      code: 'en',
      name: 'English',
      flag: '🇺🇸',
      nativeName: 'English'
    },
    {
      code: 'he',
      name: 'Hebrew',
      flag: '🇮🇱',
      nativeName: 'עברית'
    },
    {
      code: 'ar',
      name: 'Arabic',
      flag: '🇸🇦',
      nativeName: 'العربية'
    },
    {
      code: 'fr',
      name: 'French',
      flag: '🇫🇷',
      nativeName: 'Français'
    },
    {
      code: 'de',
      name: 'German',
      flag: '🇩🇪',
      nativeName: 'Deutsch'
    },
    {
      code: 'ru',
      name: 'Russian',
      flag: '🇷🇺',
      nativeName: 'Русский'
    }
  ];

  // Get role-specific display information with enhanced COD styling
  const getRoleInfo = useCallback((user) => {
    const roleMap = {
      'main_admin': { 
        display: t('mainAdmin', 'MAIN ADMIN'), 
        className: 'main-admin',
        icon: <Crown className="w-3 h-3" />,
        short: 'MA'
      },
      'manager': { 
        display: t('manager', 'MANAGER'), 
        className: 'manager',
        icon: <Star className="w-3 h-3" />,
        short: 'MGR'
      },
      'supervisor': { 
        display: t('supervisor', 'SUPERVISOR'), 
        className: 'supervisor',
        icon: <Star className="w-3 h-3" />,
        short: 'SUP'
      },
      'approver': { 
        display: t('approver', 'APPROVER'), 
        className: 'approver',
        icon: <Award className="w-3 h-3" />,
        short: 'APP'
      },
      'speedos': { 
        display: t('speedos', 'SPEEDOS'), 
        className: 'speedos',
        icon: <Package className="w-3 h-3" />,
        short: 'SPD'
      },
      'ryder': { 
        display: t('ryder', 'RYDER'), 
        className: 'ryder',
        icon: <Package className="w-3 h-3" />,
        short: 'RYD'
      },
      'verified_sponsor': { 
        display: t('verifiedSponsor', 'VERIFIED SPONSOR'), 
        className: 'verified-sponsor',
        icon: <Award className="w-3 h-3" />,
        short: 'VIP'
      },
      'player': { 
        display: t('player', 'PLAYER'), 
        className: 'player',
        icon: <Users className="w-3 h-3" />,
        short: 'PLY'
      }
    };
    return roleMap[user.role] || roleMap['player'];
  }, [t]);

  // Enhanced kick modal handlers
  const openKickModal = useCallback((participant, tournament) => {
    setKickModal({
      show: true,
      participant,
      tournament,
      loading: false
    });
  }, []);

  const closeKickModal = useCallback(() => {
    setKickModal({
      show: false,
      participant: null,
      tournament: null,
      loading: false
    });
  }, []);

  const handleKickConfirm = useCallback(async () => {
    const { participant, tournament } = kickModal;
    
    if (!participant || !tournament) {
      return;
    }
    
    setKickModal(prev => ({ ...prev, loading: true }));
    
    try {
      if (participant.type === 'real') {
        await axios.post(`/api/tournaments/${tournament._id}/kick-user/${participant._id}`, {
          reason: `Kicked by ${user.role} ${user.username}`
        });
        toast.success(t('userKickedSuccessfully', 'User kicked successfully!'));
      } else if (participant.type === 'filler') {
        await axios.post(`/api/tournaments/${tournament._id}/kick-filler/${participant._id}`, {
          reason: `Filler kicked by ${user.role} ${user.username}`
        });
        toast.success(t('fillerKickedSuccessfully', 'Filler kicked successfully!'));
      } else {
        throw new Error('Invalid participant type: ' + participant.type);
      }
      
      closeKickModal();
      loadTournaments();
      updateUserData();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToKick', 'Failed to kick participant');
      toast.error(message);
      setKickModal(prev => ({ ...prev, loading: false }));
    }
  }, [kickModal, t, user, closeKickModal]);

  // Enhanced mobile chat handlers
  const toggleMobileChat = useCallback(() => {
    setIsMobileChatActive(!isMobileChatActive);
    setMobileMenuOpen(false);
  }, [isMobileChatActive]);

  const closeMobileChat = useCallback(() => {
    setIsMobileChatActive(false);
  }, []);

  const handleChatUnreadUpdate = useCallback((count) => {
    setMobileChatUnreadCount(count);
  }, []);

  // Enhanced filler modal handlers
  const openFillerModal = useCallback((tournament) => {
    setFillerModalTournament(tournament);
    setShowFillerModal(true);
  }, []);

  const closeFillerModal = useCallback(() => {
    setShowFillerModal(false);
    setFillerModalTournament(null);
  }, []);

  const handleFillerAdded = useCallback((data) => {
    toast.success(t('fillerAddedAndTournamentUpdated', 'Filler added! Tournament updated.'));
    loadTournaments();
    updateUserData();
    closeFillerModal();
  }, [t]);

  // Enhanced language change handler
  const handleLanguageChange = useCallback((newLanguage) => {
    setMobileMenuOpen(false);
    setCurrentLanguage(newLanguage);
    localStorage.setItem('preferred-language', newLanguage);
    
    const isRtl = isRTL(newLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', newLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
    
    const languageName = languageConfig[newLanguage]?.name || newLanguage;
    toast.success(
      newLanguage === 'he' 
        ? `שפה שונתה ל${languageName}` 
        : `Language changed to ${languageName}`,
      {
        duration: 2000,
        style: {
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(234, 88, 12, 0.2))',
          color: '#fbbf24',
          border: '1px solid rgba(234, 88, 12, 0.5)',
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: '600'
        }
      }
    );
  }, []);

  // Enhanced logout handler
  const handleLogout = useCallback(() => {
    setMobileMenuOpen(false);
    onLogout();
  }, [onLogout]);

  // Enhanced profile and leaderboard navigation handlers
  const handleViewProfile = useCallback((targetUser = null) => {
    const userToView = targetUser || user;
    if (userToView) {
      navigate(`/profile/${userToView.username}`);
    }
  }, [navigate, user]);

  const handleViewLeaderboard = useCallback(() => {
    navigate('/leaderboard');
  }, [navigate]);

  // Set initial RTL direction
  useEffect(() => {
    const isRtl = isRTL(currentLanguage);
    const htmlElement = document.documentElement;
    
    htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
    htmlElement.setAttribute('lang', currentLanguage);
    
    if (isRtl) {
      htmlElement.classList.add('rtl-layout');
    } else {
      htmlElement.classList.remove('rtl-layout');
    }
  }, [currentLanguage]);

  // Enhanced data loading functions
  useEffect(() => {
    if (user) {
      loadInboxUnreadCount();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadTournaments();
      loadBracketTournaments(); // Load bracket tournaments on mount
      loadBRMultistepTournaments(); // Load BR Multistep tournaments on mount
      loadFFAMultistepTournaments(); // Load FFA Multistep tournaments on mount

      if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) {
        loadAdminStats();
      }

      const interval = setInterval(() => {
        loadTournaments();
        loadBracketTournaments(); // Refresh bracket tournaments
        loadBRMultistepTournaments();
        loadFFAMultistepTournaments();
        if (['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)) {
          loadAdminStats();
        }
      }, 4500);

      return () => {
        clearInterval(interval);
      };
    }
  }, [user]);

  // Enhanced socket event handlers with COD-style notifications
  useEffect(() => {
    if (socket && user && socketReady) {
      const handleTournamentUpdate = (data) => {
        setLastUpdateTime(Date.now());
        loadTournaments();
        loadBracketTournaments(); // Also refresh bracket tournaments
        loadBRMultistepTournaments();
        
        if (data?.type) {
          switch (data.type) {
            case 'created':
              if (data.creator !== user.inGameName) {
                if (data.tournamentType === 'giveaway') {
                  toast(`🎁 ${data.creator} ${t('createdGiveawayTournament', 'created a GIVEAWAY tournament in the lobby!')}`, {
                    icon: '🎁',
                    style: {
                      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(234, 88, 12, 0.2))',
                      color: '#fbbf24',
                      fontWeight: '700',
                      border: '2px solid #f59e0b',
                      fontFamily: "'Rajdhani', sans-serif"
                    }
                  });
                } else {
                  toast.success(`🎮 ${data.creator} ${t('createdNewMatch', 'created a new match in the lobby!')}`, {
                    style: {
                      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
                      color: '#10b981',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontWeight: '600'
                    }
                  });
                }
              }
              break;
            case 'joined':
              if (data.player !== user.inGameName) {
                toast.success(`${data.player} ${t('joinedTournament', 'joined a tournament!')}`, {
                  style: {
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(16, 185, 129, 0.2))',
                    color: '#10b981',
                    fontFamily: "'Rajdhani', sans-serif"
                  }
                });
              }
              break;
            case 'completed':
              const completedBy = data.completedByRole ? `${data.completedByRole} ${data.completedBy}` : data.completedBy;
              toast.success(`${t('tournamentCompleted', 'Tournament completed by')} ${completedBy}! ${t('winners', 'Winners')}: ${data.winners?.map(w => w.inGameName || w).join(', ')}`, {
                style: {
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(251, 191, 36, 0.2))',
                  color: '#fbbf24',
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: '700'
                }
              });
              break;
            default:
              break;
          }
        }
      };

      const handleRoleChanged = (data) => {
        setRoleChangeData(data);
        setShowRoleChangeNotification(true);
        onUserUpdate();
        toast.info(`${t('roleChangedTo', 'Your role has been changed to')} ${data.newRole} ${t('by', 'by')} ${data.changedBy}`, {
          style: {
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(124, 58, 237, 0.2))',
            color: '#a855f7',
            fontFamily: "'Orbitron', monospace"
          }
        });
      };

      const handleExpUpdate = (data) => {
        if (data.expGained > 0) {
          setExpNotification({
            expGained: data.expGained,
            source: data.source || 'Unknown',
            newLevel: data.newLevel
          });
          
          setTimeout(() => setExpNotification(null), 3000);
          
          toast.success(`+${data.expGained} EXP from ${data.source}`, {
            icon: '⭐',
            duration: 2000,
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(251, 191, 36, 0.2))',
              color: '#fbbf24',
              fontFamily: "'Orbitron', monospace"
            }
          });
        }
        updateUserData();
      };

      const handleLevelUp = (levelUpData) => {
        setLevelUpNotification(levelUpData);
        
        const titleChange = levelUpData.newTitle !== levelUpData.oldTitle;
        toast.success(
          `${t('levelUp', 'Level Up!')} ${t('level', 'Level')} ${levelUpData.newLevel}${titleChange ? ` - ${levelUpData.newTitle}` : ''}!`,
          {
            icon: '🎉',
            duration: 4000,
            style: {
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(251, 191, 36, 0.3))',
              color: '#fbbf24',
              border: '2px solid #fbbf24',
              fontFamily: "'Orbitron', monospace",
              fontWeight: '700'
            }
          }
        );
        
        updateUserData();
      };

      // Register event listeners
      socket.on('tournamentUpdated', handleTournamentUpdate);
      socket.on('roleChanged', handleRoleChanged);
      socket.on('expUpdate', handleExpUpdate);
      socket.on('levelUp', handleLevelUp);
      socket.on('brMultistepUpdated', () => loadBRMultistepTournaments());
      socket.on('brMultistepBotStatus', () => loadBRMultistepTournaments());
      socket.on('brMultistepRoomReady', () => loadBRMultistepTournaments());
      socket.on('brMultistepCommandResult', () => loadBRMultistepTournaments());
      socket.on('ffaMultistepUpdated', () => loadFFAMultistepTournaments());

      return () => {
        socket.off('tournamentUpdated', handleTournamentUpdate);
        socket.off('roleChanged', handleRoleChanged);
        socket.off('expUpdate', handleExpUpdate);
        socket.off('levelUp', handleLevelUp);
        socket.off('brMultistepUpdated');
        socket.off('brMultistepBotStatus');
        socket.off('brMultistepRoomReady');
        socket.off('brMultistepCommandResult');
        socket.off('ffaMultistepUpdated');
      };
    }
  }, [socket, user, currentLanguage, socketReady]);

  // Data loading functions
  const loadTournaments = async () => {
    try {
      const response = await axios.get('/api/tournaments');
      setTournaments(response.data);
    } catch (error) {
      toast.error(t('failedToLoadTournaments', 'Failed to load tournaments'));
    }
  };

  // Load bracket tournaments (visible to all users)
  const loadBracketTournaments = async () => {
    if (!user) return;
    try {
      const response = await axios.get('/api/bracket-tournaments');
      setBracketTournaments(response.data);
    } catch (error) {
      // Handle silently
    }
  };

  // Load BR Multistep tournaments
  const loadBRMultistepTournaments = async () => {
    if (!user) return;
    try {
      const response = await axios.get('/api/br-multistep');
      setBRMultistepTournaments(response.data);
    } catch (error) {
      // Handle silently
    }
  };

  // Load FFA Multistep tournaments
  const loadFFAMultistepTournaments = async () => {
    if (!user) return;
    try {
      const response = await axios.get('/api/ffa-multistep');
      setFFAMultistepTournaments(response.data);
    } catch (error) {
      // Handle silently
    }
  };

  // Listen for player name updates and refresh tournaments
  useEffect(() => {
    if (!socket) return;
    
    const handlePlayerNameUpdated = (data) => {
      console.log('Player name updated:', data);
      loadTournaments();
      loadBracketTournaments();
    };
    
    socket.on('playerNameUpdated', handlePlayerNameUpdated);
    
    return () => {
      socket.off('playerNameUpdated', handlePlayerNameUpdated);
    };
  }, [socket]);

  const loadAdminStats = async () => {
    try {
      const response = await axios.get('/api/admin/stats');
      setAdminStats(response.data);
    } catch (error) {
      // Handle silently
    }
  };

  const loadInboxUnreadCount = async () => {
    try {
      const response = await axios.get('/api/inbox/unread-count');
      setInboxUnreadCount(response.data.unreadCount);
    } catch (error) {
      // Handle silently
    }
  };

  const updateUserData = async () => {
    try {
      const response = await axios.post('/api/auth/verify');
      if (response.data.valid) {
        onUserUpdate(response.data.user);
      }
    } catch (error) {
      // Handle silently
    }
  };

  const handleLogin = async (credentials) => {
    const result = await onLogin(credentials);
    if (result.banned) {
      setBanModalData(result.banData);
      setShowBanModal(true);
    }
    return result;
  };

  // LiveChat user data transformation
  const transformUserForChat = useCallback((user) => {
    if (!user) return null;
    
    return {
      id: user._id || user.id,
      name: user.inGameName || user.username,
      avatar: user.avatar || '/api/placeholder/40/40',
      status: 'online',
      role: user.role,
      isAdmin: ['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role)
    };
  }, []);

  if (!user) {
    return (
      <>
        <LoginForm 
          currentLanguage={currentLanguage}
         
          onLogin={handleLogin}
          onRegister={onRegister}
          setCurrentLanguage={handleLanguageChange}
          languages={languages}
          t={t}
        />
        {showBanModal && (
          <BanModal 
            banData={banModalData}
            onClose={() => {
              setShowBanModal(false);
              setBanModalData(null);
            }}
            t={t}
          />
        )}
      </>
    );
  }

  const roleInfo = getRoleInfo(user);

  return (
    <div className="cod-tournament-container">
      {/* Enhanced COD Mobile Header */}
      <div className="tournament-header">
        <div className="header-container">
          <div className="header-main">
            {/* Mobile-First Compact Header Row */}
            <div className="mobile-header-row">
              <div className="mobile-user-section">
                <div className={`role-badge ${roleInfo.className}`}>
                  {roleInfo.icon}
                  <span className="role-text">{roleInfo.display}</span>
                  <span className="role-short">{roleInfo.short}</span>
                </div>
                
                <div className="mobile-user-info">
                  <span className="user-name">{user.inGameName}</span>
                  <div className="mobile-user-stats">
                    <div className="points-display">
                      <DollarSign className="w-3 h-3" />
                      <span>{user.points}</span>
                    </div>
                    {user.role !== 'player' && adminStats.commission && (
                      <div className="commission-display">
                        <Trophy className="w-3 h-3" />
                        <span>{adminStats.commission.userCommission || 0}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mobile-controls">
                <div className="mobile-level-display" onClick={() => setShowLevelModal(true)}>
                  <div className="mobile-level-content">
                    <div className="mobile-level-icon">
                      <Award className="w-4 h-4" />
                    </div>
                    <div className="mobile-level-info">
                      <div className="mobile-level-number">{getUserLevel()}</div>
                      <div className="mobile-level-title">
                        {getUserLevelTitle()}
                      </div>
                      <div className="mobile-level-progress">
                        <div 
                          className="mobile-level-progress-bar"
                          style={{ 
                            width: `${Math.min(100, Math.max(0, getLevelProgress()))}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile Controls Row */}
            <div className="mobile-controls-row">
              <select
                value={currentLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="language-selector"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName}
                  </option>
                ))}
              </select>
              
              <button 
                className={`mobile-chat-btn ${mobileChatUnreadCount > 0 ? 'has-unread' : ''}`}
                onClick={toggleMobileChat}
                aria-label={`${t('liveChat', 'Live Chat')}${mobileChatUnreadCount > 0 ? ` (${mobileChatUnreadCount} ${t('hasUnread', 'unread')})` : ''}`}
              >
                <MessageCircle className="w-4 h-4" />
                <span className="mobile-chat-btn-text">{t('chat', 'CHAT')}</span>
                {mobileChatUnreadCount > 0 && (
                  <span className="mobile-chat-btn-badge">
                    {mobileChatUnreadCount > 99 ? '99+' : mobileChatUnreadCount}
                  </span>
                )}
              </button>
              
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu className="w-4 h-4" />
                <span>{t('menu', 'MENU')}</span>
              </button>
            </div>

            {/* DESKTOP: Standard layout (hidden on mobile) */}
            <div className="header-user-section">
              <div className={`role-badge ${roleInfo.className}`}>
                {roleInfo.icon}
                <span className="role-text">{roleInfo.display}</span>
                <span className="role-short">{roleInfo.short}</span>
              </div>
              
              <div className="user-info">
                <span className="user-name">{user.inGameName}</span>
                <div className="user-stats">
                  <div className="points-display">
                    <DollarSign className="w-3 h-3" />
                    <span>{user.points}</span>
                  </div>
                  {user.role !== 'player' && adminStats.commission && (
                    <div className="commission-display">
                      <Trophy className="w-3 h-3" />
                      <span>{adminStats.commission.userCommission || 0}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="header-center-section">
              <LevelDisplay 
                user={user} 
                onClick={() => setShowLevelModal(true)}
                t={t}
              />
            </div>
            
            <div className="header-controls">
              <div className="connection-status">
                <div className={`connection-dot ${socket?.connected ? 'connected' : 'disconnected'}`}></div>
                <span className="connection-text">
                  {socket?.connected ? t('online', 'Online') : t('offline', 'Offline')}
                </span>
              </div>
              
              <select
                value={currentLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="language-selector"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.nativeName}
                  </option>
                ))}
              </select>
              
              <button 
                className={`mobile-chat-btn ${mobileChatUnreadCount > 0 ? 'has-unread' : ''}`}
                onClick={toggleMobileChat}
                aria-label={`${t('liveChat', 'Live Chat')}${mobileChatUnreadCount > 0 ? ` (${mobileChatUnreadCount} ${t('hasUnread', 'unread')})` : ''}`}
              >
                <MessageCircle className="w-4 h-4" />
                <span className="mobile-chat-btn-text">{t('chat', 'Chat')}</span>
                {mobileChatUnreadCount > 0 && (
                  <span className="mobile-chat-btn-badge">
                    {mobileChatUnreadCount > 99 ? '99+' : mobileChatUnreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Enhanced Navigation */}
          <div className={`main-navigation ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <button
              onClick={() => {
                setCurrentView('matches');
                setShowPayPal(false);
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'matches' ? 'active' : ''}`}
            >
              <Trophy className="w-4 h-4" />
              <span>{t('tournaments', 'TOURNAMENTS')}</span>
            </button>

            <button
              onClick={() => {
                setCurrentView('profile');
                setShowPayPal(false);
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'profile' ? 'active' : ''}`}
            >
              <User className="w-4 h-4" />
              <span>{t('profile', 'PROFILE')}</span>
            </button>

            <button
              onClick={() => {
                setCurrentView('points');
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'points' ? 'active' : ''}`}
            >
              <DollarSign className="w-4 h-4" />
              <span>{t('points', 'POINTS')}</span>
            </button>

            <button
              onClick={() => {
                setCurrentView('levels');
                setShowPayPal(false);
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'levels' ? 'active' : ''}`}
            >
              <Award className="w-4 h-4" />
              <span>{t('levelsBadges', 'LEVEL & BADGES')}</span>
            </button>

            <button
              onClick={() => {
                setCurrentView('history');
                setShowPayPal(false);
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'history' ? 'active' : ''}`}
            >
              <Calendar className="w-4 h-4" />
              <span>{t('matchHistory', 'HISTORY')}</span>
            </button>

            <button
              onClick={() => {
                setCurrentView('inbox');
                setShowPayPal(false);
                setMobileMenuOpen(false);
              }}
              className={`nav-button ${currentView === 'inbox' ? 'active' : ''}`}
            >
              <Inbox className="w-4 h-4" />
              <span>{t('supportInbox', 'SUPPORT')}</span>
              {inboxUnreadCount > 0 && (
                <span className="nav-badge">{inboxUnreadCount}</span>
              )}
            </button>
            
            {['main_admin', 'manager', 'supervisor', 'approver'].includes(user.role) && (
              <button
                onClick={() => {
                  setCurrentView('admin');
                  setShowPayPal(false);
                  setMobileMenuOpen(false);
                }}
                className={`nav-button ${currentView === 'admin' ? 'active' : ''}`}
              >
                <Shield className="w-4 h-4" />
                <span>
                  {user.role === 'main_admin' ? t('admin', 'ADMIN') : 
                   user.role === 'manager' ? t('manager', 'MANAGER') : 
                   user.role === 'supervisor' ? t('supervisor', 'SUPERVISOR') : t('approver', 'APPROVER')}
                </span>
                {adminStats.pendingApproval > 0 && (
                  <span className="nav-badge">{adminStats.pendingApproval}</span>
                )}
              </button>
            )}
            
            {["speedos", "ryder"].includes(user.role) && (
              <button
                onClick={() => {
                  setCurrentView("vendor");
                  setShowPayPal(false);
                  setMobileMenuOpen(false);
                }}
                className={`nav-button ${currentView === "vendor" ? "active" : ""}`}
              >
                <Package className="w-4 h-4" />
                <span>
                  {user.role === "speedos" ? t("speedos", "SPEEDOS") : t("ryder", "RYDER")}
                </span>
              </button>
            )}

            <button
              onClick={() => {
                handleViewProfile();
                setMobileMenuOpen(false);
              }}
              className="nav-button"
            >
              <User className="w-4 h-4" />
              <span>{t('publicProfile', 'PUBLIC')}</span>
            </button>

            <button
              onClick={handleLogout}
              className="nav-button logout-nav-button"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('logout', 'LOGOUT')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Main Content */}
      <div className="main-content">
        {currentView === 'matches' && (
          <div className="content-section">
            <CreateTournament
              user={user}
              onTournamentCreated={loadTournaments}
              onUserUpdate={updateUserData}
              t={t}
            />

            {/* Bracket Tournament Section - Visible to all, create only for admins */}
            <div className="bracket-tournaments-section">
              <div className="bracket-section-header">
                <button
                  className="bracket-toggle-btn green-theme"
                  onClick={() => setShowBracketSection(!showBracketSection)}
                >
                  <Trophy className="w-5 h-5 text-emerald-400" />
                  <span>{t('multistepTournaments', 'Multistep Tournaments')}</span>
                  <span className="bracket-count green">({bracketTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length + brMultistepTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length + ffaMultistepTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length})</span>
                </button>
                {showBracketSection && ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(user?.role) && (
                  <button
                    className="bracket-create-btn green-theme"
                    onClick={() => setShowMultistepTypeSelector(true)}
                  >
                    <Plus className="w-4 h-4" />
                    {t('createBracket', 'Create')}
                  </button>
                )}
              </div>

              {showBracketSection && (
                <div className="bracket-tournaments-list">
                  {(bracketTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length + brMultistepTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length + ffaMultistepTournaments.filter(t => t.status !== 'cancelled' && t.status !== 'completed').length) === 0 ? (
                    <div className="bracket-empty">
                      <Trophy className="w-8 h-8 text-gray-500" />
                      <p>{t('noMultistepTournaments', 'No multistep tournaments yet')}</p>
                    </div>
                  ) : (
                    <>
                      {bracketTournaments
                        .filter(t => t.status !== 'cancelled' && t.status !== 'completed')
                        .map(tournament => (
                          <BracketTournamentCard
                            key={tournament._id}
                            tournament={tournament}
                            user={user}
                            onUpdate={(updatedTournament) => {
                              setBracketTournaments(prev =>
                                prev.map(t => t._id === updatedTournament._id ? updatedTournament : t)
                              );
                              updateUserData();
                            }}
                            t={t}
                          />
                        ))}
                      {brMultistepTournaments
                        .filter(t => t.status !== 'cancelled' && t.status !== 'completed')
                        .map(tournament => (
                          <BRMultistepCard
                            socket={socket}
                            key={tournament._id}
                            tournament={tournament}
                            user={user}
                            onUpdate={(updatedTournament) => {
                              setBRMultistepTournaments(prev =>
                                prev.map(t => t._id === updatedTournament._id ? updatedTournament : t)
                              );
                              loadBRMultistepTournaments();
                              updateUserData();
                            }}
                            t={t}
                            currentLanguage={currentLanguage}
                          />
                        ))}
                      {ffaMultistepTournaments
                        .filter(t => t.status !== 'cancelled' && t.status !== 'completed')
                        .map(tournament => (
                          <FFAMultistepCard
                            socket={socket}
                            key={tournament._id}
                            tournament={tournament}
                            user={user}
                            onUpdate={() => loadFFAMultistepTournaments()}
                            t={t}
                            currentLanguage={currentLanguage}
                          />
                        ))}
                    </>
                  )}
                </div>
              )}
            </div>

            <TournamentsList
              tournaments={tournaments}
              bracketTournaments={bracketTournaments}
              brMultistepTournaments={brMultistepTournaments}
              ffaMultistepTournaments={ffaMultistepTournaments}
              user={user}
              onTournamentUpdate={loadTournaments}
              onBracketTournamentUpdate={(updatedTournament) => {
                setBracketTournaments(prev =>
                  prev.map(t => t._id === updatedTournament._id ? updatedTournament : t)
                );
                updateUserData();
              }}
              onUserUpdate={updateUserData}
              socket={socket}
              socketReady={socketReady}
              lastUpdateTime={lastUpdateTime}
              setLastUpdateTime={setLastUpdateTime}
              loadTournaments={loadTournaments}
              onOpenFillerModal={openFillerModal}
              onOpenKickModal={openKickModal}
              cleanAdminName={cleanAdminName}
              t={t}
            />
          </div>
        )}
        
        {currentView === 'profile' && (
          <div className="content-section">
            <ProfileManagement
              user={user}
              onUserUpdate={updateUserData}
              t={t}
            />
          </div>
        )}

        {currentView === 'points' && (
          <div className="content-section">
            <PointsManagement
              user={user}
              onUserUpdate={updateUserData}
              onShowPayPal={() => setShowPayPal(true)}
              t={t}
              language={currentLanguage}
            />
          </div>
        )}

        {currentView === 'levels' && (
          <div className="content-section">
            <div style={{ display: 'grid', gap: '20px' }}>
              <LevelProgress 
                user={user}
                showDetails={true}
                t={t}
              />
              <BadgeDisplay 
                user={user}
                showProgress={true}
                maxDisplay={15}
                t={t}
              />
            </div>
          </div>
        )}

        {currentView === 'history' && (
          <div className="content-section">
            <MatchHistory 
              user={user}
              t={t}
            />
          </div>
        )}

        {currentView === 'inbox' && (
          <div className="content-section">
            <InboxSystem 
              user={user}
              socket={socket}
              t={t}
            />
          </div>
        )}
        
        {currentView === 'admin' && user.role !== 'player' && (
          <AdminPanel 
            user={user}
            tournaments={tournaments}
            onTournamentUpdate={loadTournaments}
            onUserUpdate={updateUserData}
            socket={socket}
            t={t}
          />
        )}
        
        {currentView === 'vendor' && ['speedos', 'ryder'].includes(user.role) && (
          <VendorDashboard 
            user={user}
            language={currentLanguage}
          />
        )}
      </div>

      {/* Enhanced LiveChat */}
      <LiveChat 
        currentUser={transformUserForChat(user)}
        onlineUsers={onlineUsers}
        socket={socket}
        connectionStatus={connectionStatus}
        socketReady={socketReady}
        isMobileChatActive={isMobileChatActive}
        onCloseMobileChat={closeMobileChat}
        onUnreadCountUpdate={handleChatUnreadUpdate}
        t={t}
      />

      {/* Enhanced Modals and Notifications */}
      {showPayPal && currentView === 'points' && (
        <PayPalModal 
          onClose={() => setShowPayPal(false)}
          onSuccess={updateUserData}
          t={t}
        />
      )}

      {showFillerModal && fillerModalTournament && (
        <AddFillerModal
          tournament={fillerModalTournament}
          user={user}
          onClose={closeFillerModal}
          onFillerAdded={handleFillerAdded}
          t={t}
        />
      )}

      {kickModal.show && (
        <KickModal
          participant={kickModal.participant}
          tournament={kickModal.tournament}
          onConfirm={handleKickConfirm}
          onCancel={closeKickModal}
          loading={kickModal.loading}
          cleanAdminName={cleanAdminName}
          t={t}
        />
      )}

      {/* Multistep Type Selector Modal */}
      {showMultistepTypeSelector && (
        <MultistepTypeSelector
          onSelect={(type) => {
            setShowMultistepTypeSelector(false);
            if (type === 'mp_multistep') {
              setShowBracketCreate(true);
            } else if (type === 'br_multistep') {
              setShowBRMultistepCreate(true);
            } else if (type === 'ffa_multistep') {
              setShowFFAMultistepCreate(true);
            }
          }}
          onClose={() => setShowMultistepTypeSelector(false)}
          t={t}
        />
      )}

      {/* MP Multistep (Bracket) Tournament Create Modal */}
      {showBracketCreate && (
        <BracketTournamentCreate
          user={user}
          onClose={() => setShowBracketCreate(false)}
          onCreated={(tournament, newPoints) => {
            setBracketTournaments(prev => [tournament, ...prev]);
            updateUserData();
          }}
          t={t}
        />
      )}

      {/* BR Multistep Tournament Create Modal */}
      {showBRMultistepCreate && (
        <BRMultistepCreate
          user={user}
          onClose={() => setShowBRMultistepCreate(false)}
          onCreated={(tournament, newPoints) => {
            setBRMultistepTournaments(prev => [tournament, ...prev]);
            updateUserData();
          }}
          t={t}
        />
      )}

      {/* FFA Multistep Tournament Create Modal */}
      {showFFAMultistepCreate && (
        <FFAMultistepCreate
          user={user}
          onClose={() => setShowFFAMultistepCreate(false)}
          onCreated={(tournament) => {
            setFFAMultistepTournaments(prev => [tournament, ...prev]);
            updateUserData();
          }}
          t={t}
        />
      )}

      {/* Enhanced Notifications */}
      {showRoleChangeNotification && roleChangeData && (
        <div className="role-change-modal card">
          <div className="role-change-content">
            <div className="role-change-header">
              <Shield className="w-5 h-5 text-blue-400" />
              <h3 className="military-font text-glow">{t('roleUpdated', 'ROLE UPDATED')}</h3>
            </div>
            
            <div className="role-change-body">
              <p>{t('roleChangedTo', 'Your role has been changed to')} <strong className="text-glow">{roleChangeData.newRole}</strong></p>
              <p className="role-change-meta">{t('changedBy', 'Changed by')}: <strong>{roleChangeData.changedBy}</strong></p>
              {roleChangeData.reason && (
                <p className="role-change-meta"><em>{t('reason', 'Reason')}: {roleChangeData.reason}</em></p>
              )}
            </div>
            
            <button
              onClick={() => {
                setShowRoleChangeNotification(false);
                setRoleChangeData(null);
              }}
              className="role-change-button tactical-border border-glow"
            >
              {t('gotIt', 'COPY THAT!')}
            </button>
          </div>
        </div>
      )}

      {levelUpNotification && (
        <div className="level-up-notification card">
          <div className="level-up-icon">🎉</div>
          <h2 className="level-up-title military-font text-glow">{t('levelUp', 'LEVEL UP!')}</h2>
          <p className="level-up-subtitle combat-font">{t('congratulations', 'CONGRATULATIONS!')}</p>
          
          <div className="level-up-details">
            <div className="level-up-level-change">
              <span>{t('level', 'Level')} {levelUpNotification.oldLevel}</span>
              <span className="level-up-arrow">→</span>
              <span className="text-glow">{t('level', 'Level')} {levelUpNotification.newLevel}</span>
            </div>
            
            {levelUpNotification.titleChanged && (
              <div className="level-up-title-change text-glow">
                {levelUpNotification.oldTitle} → {levelUpNotification.newTitle}
              </div>
            )}
          </div>
          
          <button
            onClick={() => setLevelUpNotification(null)}
            className="level-up-close tactical-border border-glow"
          >
            {t('awesome', 'AWESOME!')}
          </button>
        </div>
      )}

      {badgeNotification && (
        <div className="badge-unlock-notification card">
          <div className="badge-unlock-header">
            <div className="badge-unlock-icon">
              {badgeNotification.icon || '🏅'}
            </div>
            <div className="badge-unlock-info">
              <div className="badge-unlock-title military-font">
                {t('badgeUnlocked', 'BADGE UNLOCKED')}
              </div>
              <div className="badge-unlock-name text-glow">
                {badgeNotification.badgeName}
              </div>
            </div>
          </div>
          
          <div className="badge-unlock-description">
            {badgeNotification.description}
          </div>
          
          <div className="badge-unlock-exp">
            <Star className="w-3 h-3" />
            <span className="text-glow">+{badgeNotification.expReward} EXP</span>
          </div>
        </div>
      )}

      {expNotification && (
        <div className="exp-gain-notification">
          <span className="text-glow">+{expNotification.expGained} EXP from {expNotification.source}</span>
        </div>
      )}

      {showLevelModal && (
        <div className="badge-modal-overlay" onClick={() => setShowLevelModal(false)}>
          <div className="badge-modal card" onClick={(e) => e.stopPropagation()}>
            <div className="badge-modal-header">
              <div className="badge-modal-info">
                <h3 className="badge-modal-title military-font text-glow">{t('levelProgress', 'LEVEL PROGRESS')}</h3>
              </div>
              <button 
                className="badge-modal-close tactical-border" 
                onClick={() => setShowLevelModal(false)}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <LevelProgress 
              user={user}
              showDetails={true}
              t={t}
            />
          </div>
        </div>
      )}
      {/* Footer */}
      <div className="site-footer">
        <span>𓅪 Managed by Kenta 𓅪</span>
      </div>
    </div>
  );
};

export default CODTournamentSystem;